// ============================================================
// PortalDetail — the saved-portal workspace (reopen a portal).
//
// Sections: Portal Overview, Active Modules, Readiness, Actions, Blockers,
// Notes, Recent Receipts, Settings, and the derived Next Recommended Build
// Step. Every list is REAL (from the API) — no fabricated data, no fake
// completed actions, no fake blocker resolutions. All actions are in place
// (No Dead Ends). Presentational + SSR-safe; the route wrapper owns the
// usePortal hook + performs the persisted mutations.
// ============================================================

import { COMMAND_CENTER_ROOT } from '../../commandCenterRoutes'
import { useState, type ReactNode } from 'react'
import CommandCenterShell from '../../shell/CommandCenterShell'
import { CC_INK, CC_MONO } from '../../shell'
import {
  MODULE_CATALOG, moduleCatalogEntry, READINESS_STATUSES, READINESS_META,
  PROJECT_TYPE_META, OLIVIUM_ACCENT, summarizePortal, nextBuildStep,
  type PortalDetail as PortalDetailData, type ActionStatus, type ActionPriority,
  type BlockerSeverity, type ReadinessStatus,
} from './portalFactory'
import {
  PORTAL_HONESTY, ModuleStatusBadge, ReadinessPill, SectionCard, StateBlock,
  BackendNotReadyBanner, primaryBtn, chipBtn, inputStyle,
} from './PortalKit'

/** Mutation callbacks resolve to false when the write failed so an inline
 *  input only clears on a real save (a failed write is never a silent no-op). */
type MutationResult = void | Promise<boolean>

export interface PortalDetailProps {
  detail: PortalDetailData | null
  loading: boolean
  error: string | null
  authRequired: boolean
  notFound: boolean
  /** Honest surface for a rejected mutation (from the route wrapper). */
  mutationError?: string | null
  onHome?: () => void
  onBackCommandCenter?: () => void
  onOpenFactory?: () => void
  onOpenRegistry?: () => void
  onOpenModule?: (moduleKey: string) => void
  onOpenLink?: (url: string) => void
  onOpenContext?: () => void
  onOpenRepo?: () => void
  /** PHASE_2M P7 — issue a Secure Access code prefilled for THIS portal. */
  onIssueAccess?: () => void
  /** PHASE_2M P7 — open the Olivium Secure Access hub (revoke/expire live there). */
  onOpenSecureAccess?: () => void
  onCreateAction?: (input: { title: string; priority: ActionPriority }) => MutationResult
  onCycleAction?: (id: string, status: ActionStatus) => void
  onCreateNote?: (body: string) => MutationResult
  onCreateBlocker?: (input: { summary: string; severity: BlockerSeverity }) => MutationResult
  onResolveBlocker?: (id: string, resolution: string) => void
  onAttachModule?: (moduleKey: string) => void
  onSetReadiness?: (status: ReadinessStatus) => void
  onCreateReceipt?: (summary: string) => MutationResult
  /** The operator's canonical projects, for the link selector. */
  projects?: Array<{ id: string; name: string }>
  /** Honest signal that the projects list failed to load (vs genuinely empty). */
  projectsError?: string | null
  /** Link (uuid) or unlink (null) this portal's canonical project. */
  onSetProjectLink?: (project_id: string | null) => MutationResult
  /** Navigate to a linked canonical project's detail. */
  onOpenProject?: (project_id: string) => void
  /** End User Portal — publish/unpublish a module to the recipient portal. */
  onSetModulePublished?: (module: { id: string; config: Record<string, unknown> | null }, published: boolean) => MutationResult
  /** End User Portal — open the exact recipient preview (server manifest). */
  onPreviewRecipient?: () => void
  /** End User Portal — the recipient-access lifecycle panel (route-owned). */
  recipientAccess?: ReactNode
}

/** Canonical-project link control: shows the bound project (with open/unlink)
 *  or a selector to bind one. Presentational; ownership is enforced server-side. */
function CanonicalProjectCard({ detail, projects, projectsError, onSetProjectLink, onOpenProject }: {
  detail: PortalDetailData
  projects?: Array<{ id: string; name: string }>
  projectsError?: string | null
  onSetProjectLink?: (project_id: string | null) => MutationResult
  onOpenProject?: (project_id: string) => void
}) {
  const [pick, setPick] = useState('')
  const linked = detail.linked_project
  return (
    <SectionCard title="Canonical Project" accent="#7c3aed">
      {linked ? (
        <div>
          <p style={{ margin: '0 0 8px', fontSize: 11, color: CC_INK.muted, lineHeight: 1.5 }}>
            This portal is the presentation layer for a canonical project.
          </p>
          <div style={{ fontSize: 13, color: CC_INK.primary, fontWeight: 600 }}>{linked.name}</div>
          <div style={{ fontSize: 10, color: CC_INK.faint, fontFamily: CC_MONO, marginTop: 2 }}>{linked.slug} · {linked.status}</div>
          <div style={{ display: 'flex', gap: 6, marginTop: 10 }}>
            {onOpenProject && <button type="button" style={chipBtn} onClick={() => onOpenProject(linked.id)}>Open project →</button>}
            <button type="button" style={chipBtn} onClick={() => void onSetProjectLink?.(null)}>Unlink</button>
          </div>
        </div>
      ) : projects && projects.length > 0 ? (
        <div>
          <p style={{ margin: '0 0 8px', fontSize: 11, color: CC_INK.muted, lineHeight: 1.5 }}>
            Standalone. Link it to a canonical project you own — linking never publishes internal content.
          </p>
          <select style={inputStyle} aria-label="Link this portal to a canonical project" value={pick} onChange={(e) => setPick(e.target.value)}>
            <option value="">Choose a project…</option>
            {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
          <button type="button" style={{ ...chipBtn, marginTop: 8 }} disabled={!pick} onClick={() => { if (pick) void onSetProjectLink?.(pick) }}>Link project</button>
        </div>
      ) : projectsError ? (
        <p style={{ margin: 0, fontSize: 11, color: '#dc2626', lineHeight: 1.5 }}>
          Standalone. Couldn&rsquo;t load your projects to link ({projectsError}) — reopen the portal to retry.
        </p>
      ) : (
        <p style={{ margin: 0, fontSize: 11, color: CC_INK.muted, lineHeight: 1.5 }}>
          Standalone — not linked to a canonical project. Create a project first to link this portal to it.
        </p>
      )}
    </SectionCard>
  )
}

const nextActionStatus: Record<ActionStatus, ActionStatus> = { open: 'in_progress', in_progress: 'done', done: 'blocked', blocked: 'open' }

function InlineAdd({ placeholder, cta, onAdd, wide }: { placeholder: string; cta: string; onAdd: (v: string) => void | Promise<boolean>; wide?: boolean }) {
  const [v, setV] = useState('')
  // Clear only after a real save — a rejected write keeps the text so nothing
  // looks accepted when it was not.
  const submit = async () => { if (!v.trim()) return; const ok = await onAdd(v.trim()); if (ok !== false) setV('') }
  return (
    <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
      {wide
        ? <textarea style={{ ...inputStyle, minHeight: 40, resize: 'vertical' }} value={v} placeholder={placeholder} onChange={(e) => setV(e.target.value)} />
        : <input style={inputStyle} value={v} placeholder={placeholder} onChange={(e) => setV(e.target.value)} />}
      <button type="button" style={chipBtn} onClick={submit}>{cta}</button>
    </div>
  )
}

export default function PortalDetail(props: PortalDetailProps) {
  const { detail, loading, error, authRequired, notFound, onHome, onBackCommandCenter, onOpenFactory, onOpenRegistry, onOpenModule, onOpenLink } = props
  const [blockerText, setBlockerText] = useState('')
  const backendNotReady = !!error && /not migrated/i.test(error)

  // Non-detail states — render the shell with an honest block.
  if (!detail) {
    return (
      <CommandCenterShell
        onHome={onHome} onBackCommandCenter={onBackCommandCenter}
        parentBack={{ label: '← Registry', onClick: onOpenRegistry }}
        breadcrumb={['OLIVIUM', 'PORTAL FACTORY', 'PORTAL']}
        title={notFound ? 'Portal not found' : 'Portal'} titleAccent={OLIVIUM_ACCENT} honestyBadge={PORTAL_HONESTY}
      >
        {backendNotReady && <BackendNotReadyBanner message={error!} />}
        {loading && <StateBlock kind="loading" title="Loading portal…" />}
        {!loading && authRequired && <StateBlock kind="auth" title="Sign in to open this portal" body="Portals are scoped to your authenticated cockpit session." />}
        {!loading && notFound && <StateBlock kind="empty" title="Portal not found" body="It may have been removed, or it belongs to another operator." />}
        {!loading && !authRequired && !notFound && error && !backendNotReady && <StateBlock kind="error" title="Could not open portal" body={error} />}
        {!loading && (notFound || error) && <div style={{ marginTop: 14 }}><button type="button" style={chipBtn} onClick={onOpenRegistry}>← Back to registry</button></div>}
      </CommandCenterShell>
    )
  }

  const s = summarizePortal(detail)
  const attachedKeys = new Set(detail.modules.map((m) => m.module_key))
  const attachable = MODULE_CATALOG.filter((m) => !attachedKeys.has(m.key))

  const rightRail = (
    <div>
      <SectionCard title="Readiness status" accent={READINESS_META[detail.portal.readiness_status].accent}>
        <div style={{ marginBottom: 10 }}><ReadinessPill status={detail.portal.readiness_status} /></div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          {READINESS_STATUSES.map((r) => (
            <button key={r} type="button" onClick={() => props.onSetReadiness?.(r)} style={{ ...chipBtn, fontSize: 10, padding: '4px 8px', ...(detail.portal.readiness_status === r ? { color: READINESS_META[r].accent, borderColor: READINESS_META[r].accent + '55' } : {}) }}>
              {READINESS_META[r].label}
            </button>
          ))}
        </div>
      </SectionCard>
      <SectionCard title="At a glance">
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, fontSize: 11, color: CC_INK.secondary }}>
          <div>{s.modules} modules</div>
          <div>{s.openActions} open actions</div>
          <div>{s.openBlockers} blockers</div>
          <div>{s.notes} notes</div>
          <div>{s.receipts} receipts</div>
          <div>{s.doneActions} done</div>
        </div>
      </SectionCard>
      <CanonicalProjectCard
        detail={detail}
        projects={props.projects}
        projectsError={props.projectsError}
        onSetProjectLink={props.onSetProjectLink}
        onOpenProject={props.onOpenProject}
      />
      <SectionCard title="Client Access" accent="#0891b2">
        <p style={{ margin: '0 0 9px', fontSize: 11, color: CC_INK.muted, lineHeight: 1.55 }}>
          Private by default. Publishing (client-visible flag + receipt) lives on the client-access bridge; codes are hash-at-rest, one-time reveal, revoke/expire audited.
        </p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <button type="button" style={chipBtn} onClick={props.onOpenContext}>Open client-access bridge</button>
          {props.onPreviewRecipient && (
            <button type="button" style={chipBtn} onClick={props.onPreviewRecipient}>Preview as recipient →</button>
          )}
          <button type="button" style={chipBtn} onClick={props.onIssueAccess}>Issue secure access code</button>
          <button type="button" style={chipBtn} onClick={props.onOpenSecureAccess}>View access codes / revoke</button>
          <button
            type="button"
            style={chipBtn}
            onClick={() => {
              const p = `${COMMAND_CENTER_ROOT}/olivium/portals/${detail.portal.id}`
              void navigator.clipboard?.writeText(typeof window !== 'undefined' ? `${window.location.origin}${p}` : p)
            }}
          >
            Copy portal link
          </button>
        </div>
        <p style={{ margin: '9px 0 0', fontSize: 10, color: '#b8862a', fontFamily: CC_MONO }}>
          MEMBERS · BACKEND_DEFERRED — portals have no member roster table yet; access is code-based. Never faked.
        </p>
      </SectionCard>
      <SectionCard title="Next recommended build step" accent="#059669">
        <p style={{ margin: 0, fontSize: 12, color: CC_INK.secondary, lineHeight: 1.5 }}>{nextBuildStep(detail)}</p>
      </SectionCard>
    </div>
  )

  return (
    <CommandCenterShell
      onHome={onHome} onBackCommandCenter={onBackCommandCenter}
      parentBack={{ label: '← Registry', onClick: onOpenRegistry }}
      breadcrumb={['OLIVIUM', 'PORTAL FACTORY', detail.portal.project_slug.toUpperCase()]}
      title={detail.portal.project_name} titleAccent={OLIVIUM_ACCENT}
      statusBadge={<span style={{ marginLeft: 8 }}><ReadinessPill status={detail.portal.readiness_status} /></span>}
      honestyBadge={PORTAL_HONESTY}
      rightRail={rightRail}
    >
      <div>
        {/* Honest surface for a rejected mutation — never a silent no-op. */}
        {props.mutationError && <div style={{ marginBottom: 14 }}><StateBlock kind="error" title="Last action failed" body={props.mutationError} /></div>}

        {/* Portal Overview */}
        <SectionCard title="Portal Overview">
          <div style={{ fontSize: 10, color: CC_INK.faint, fontFamily: CC_MONO, marginBottom: 8 }}>
            {PROJECT_TYPE_META[detail.portal.project_type]?.label ?? detail.portal.project_type} · {detail.portal.visibility_status} · {detail.portal.portal_template}
          </div>
          <p style={{ margin: 0, fontSize: 13, color: CC_INK.secondary, lineHeight: 1.6 }}>{detail.portal.summary || <em style={{ color: CC_INK.faint }}>No summary yet.</em>}</p>
          {detail.portal.owner_label && <div style={{ marginTop: 8, fontSize: 11, color: CC_INK.muted }}>Owner · {detail.portal.owner_label}</div>}
        </SectionCard>

        {/* Recipient Access (End User Portal Activation) — the full recipient
            lifecycle: issue, copy link, status, suspend/restore, revoke, reissue. */}
        {props.recipientAccess}

        {/* Active Modules */}
        <SectionCard title="Active Modules" right={<span style={{ fontSize: 10, color: CC_INK.faint, fontFamily: CC_MONO }}>{detail.modules.length} attached</span>}>
          {detail.modules.length === 0 && <StateBlock kind="empty" title="No modules attached yet" body="Attach a module below." />}
          {detail.modules.length > 0 && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 10 }}>
              {detail.modules.map((m) => {
                const entry = moduleCatalogEntry(m.module_key)
                return (
                  <div key={m.id} style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.07)', borderRadius: 10, padding: 11 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
                      <strong style={{ fontSize: 12, color: CC_INK.primary, flex: 1 }}>{m.module_label}</strong>
                      <ModuleStatusBadge status={m.module_status} />
                    </div>
                    <p style={{ margin: '0 0 8px', fontSize: 10.5, color: CC_INK.muted, lineHeight: 1.4 }}>{entry?.purpose ?? ''}</p>
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                      <button type="button" style={{ ...chipBtn, fontSize: 10, padding: '4px 8px' }} onClick={() => onOpenModule?.(m.module_key)}>Open module →</button>
                      {m.link_target && <button type="button" style={{ ...chipBtn, fontSize: 10, padding: '4px 8px' }} onClick={() => onOpenLink?.(m.link_target!)}>Open surface</button>}
                    </div>
                    {/* Explicit client publishing — nothing is ever auto-published.
                        Enforcement is server-side (unpublished = 404 for recipients). */}
                    {props.onSetModulePublished && (
                      <label style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 8, fontSize: 10.5, color: (m.config as any)?.client_published === true ? '#0891b2' : CC_INK.muted, cursor: 'pointer' }}>
                        <input
                          type="checkbox"
                          checked={(m.config as any)?.client_published === true}
                          onChange={(e) => void props.onSetModulePublished?.({ id: m.id, config: m.config }, e.target.checked)}
                        />
                        Published to client portal
                      </label>
                    )}
                  </div>
                )
              })}
            </div>
          )}
          {attachable.length > 0 && (
            <div style={{ marginTop: 12 }}>
              <div style={{ fontSize: 10, color: CC_INK.faint, fontFamily: CC_MONO, marginBottom: 6 }}>Attach a module</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {attachable.map((m) => (
                  <button key={m.key} type="button" style={{ ...chipBtn, fontSize: 11 }} onClick={() => props.onAttachModule?.(m.key)} title={m.purpose}>{`+ ${m.label}`}</button>
                ))}
              </div>
            </div>
          )}
        </SectionCard>

        {/* Current Actions */}
        <SectionCard title="Current Actions" right={<span style={{ fontSize: 10, color: CC_INK.faint, fontFamily: CC_MONO }}>{s.openActions} open</span>}>
          {detail.actions.length === 0 && <StateBlock kind="empty" title="No actions yet" body="Add the first action below." />}
          {detail.actions.map((a) => (
            <div key={a.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
              <button type="button" onClick={() => props.onCycleAction?.(a.id, nextActionStatus[a.status])} title="Cycle status" style={{ ...chipBtn, fontSize: 9, padding: '3px 7px', minWidth: 92 }}>{a.status.replace('_', ' ').toUpperCase()}</button>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 12.5, color: a.status === 'done' ? CC_INK.faint : CC_INK.primary, textDecoration: a.status === 'done' ? 'line-through' : 'none' }}>{a.title}</div>
                {a.detail && <div style={{ fontSize: 10.5, color: CC_INK.muted }}>{a.detail}</div>}
              </div>
              <span style={{ fontSize: 9, color: CC_INK.faint, fontFamily: CC_MONO }}>{a.priority}</span>
            </div>
          ))}
          <InlineAdd placeholder="New action…" cta="+ Action" onAdd={(v) => props.onCreateAction?.({ title: v, priority: 'normal' })} />
        </SectionCard>

        {/* Blockers */}
        <SectionCard title="Blockers" right={<span style={{ fontSize: 10, color: CC_INK.faint, fontFamily: CC_MONO }}>{s.openBlockers} open</span>}>
          {detail.blockers.length === 0 && <StateBlock kind="empty" title="No blockers" body="Nothing is flagged blocked right now." />}
          {detail.blockers.map((b) => (
            <div key={b.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
              <span style={{ fontSize: 9, fontFamily: CC_MONO, fontWeight: 800, color: b.status === 'resolved' ? '#059669' : '#dc2626', minWidth: 68 }}>{b.status === 'resolved' ? 'RESOLVED' : b.severity.toUpperCase()}</span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 12.5, color: b.status === 'resolved' ? CC_INK.faint : CC_INK.primary }}>{b.summary}</div>
                {b.resolution && <div style={{ fontSize: 10.5, color: CC_INK.muted }}>→ {b.resolution}</div>}
              </div>
              {b.status === 'open' && <button type="button" style={{ ...chipBtn, fontSize: 10, padding: '4px 8px' }} onClick={() => props.onResolveBlocker?.(b.id, 'Resolved by operator')}>Resolve</button>}
            </div>
          ))}
          <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
            <input style={inputStyle} value={blockerText} placeholder="Flag a blocker…" onChange={(e) => setBlockerText(e.target.value)} />
            <button type="button" style={chipBtn} onClick={async () => { if (!blockerText.trim()) return; const ok = await props.onCreateBlocker?.({ summary: blockerText.trim(), severity: 'medium' }); if (ok !== false) setBlockerText('') }}>⚑ Flag</button>
          </div>
        </SectionCard>

        {/* Notes */}
        <SectionCard title="Notes" right={<span style={{ fontSize: 10, color: CC_INK.faint, fontFamily: CC_MONO }}>{s.notes}</span>}>
          {detail.notes.length === 0 && <StateBlock kind="empty" title="No notes yet" body="Add a note to the log below." />}
          {detail.notes.map((n) => (
            <div key={n.id} style={{ padding: '7px 0', borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
              <div style={{ fontSize: 12, color: CC_INK.secondary, lineHeight: 1.5 }}>{n.body}</div>
              <div style={{ fontSize: 9, color: CC_INK.faint, fontFamily: CC_MONO, marginTop: 2 }}>{n.created_at.slice(0, 10)}</div>
            </div>
          ))}
          <InlineAdd placeholder="Add a note…" cta="+ Note" wide onAdd={(v) => props.onCreateNote?.(v)} />
        </SectionCard>

        {/* Recent Receipts */}
        <SectionCard title="Recent Receipts" right={<span style={{ fontSize: 10, color: CC_INK.faint, fontFamily: CC_MONO }}>{s.receipts}</span>}>
          {detail.receipts.length === 0 && <StateBlock kind="empty" title="No receipts yet" body="Receipts are written automatically as you work the portal." />}
          {detail.receipts.slice(0, 12).map((r) => (
            <div key={r.id} style={{ display: 'flex', gap: 10, padding: '6px 0', borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
              <span style={{ fontSize: 9, fontFamily: CC_MONO, color: '#059669', minWidth: 120 }}>{r.receipt_type}</span>
              <span style={{ flex: 1, fontSize: 11.5, color: CC_INK.secondary }}>{r.summary}</span>
              <span style={{ fontSize: 9, color: CC_INK.faint, fontFamily: CC_MONO }}>{r.created_at.slice(0, 10)}</span>
            </div>
          ))}
          <InlineAdd placeholder="Author a milestone receipt…" cta="+ Receipt" onAdd={(v) => props.onCreateReceipt?.(v)} />
        </SectionCard>

        {/* Project Context + Repo (PHASE_2L) */}
        <SectionCard title="Project Context" hint="Repo + deployment metadata, PR/build report (honest — live sync deferred), next action, and the client-access bridge (private by default).">
          <div style={{ display: 'flex', gap: 8 }}>
            <button type="button" style={chipBtn} onClick={() => props.onOpenContext?.()}>Open project context →</button>
            <button type="button" style={chipBtn} onClick={() => props.onOpenRepo?.()}>Edit repo &amp; deployment →</button>
          </div>
        </SectionCard>

        {/* Portal Settings */}
        <SectionCard title="Portal Settings" hint="Readiness is changed from the right rail. Deeper settings (template, visibility) edit through the create/update API.">
          <div style={{ fontSize: 11, color: CC_INK.muted, fontFamily: CC_MONO }}>
            slug {detail.portal.project_slug} · created {detail.portal.created_at.slice(0, 10)} · updated {detail.portal.updated_at.slice(0, 10)}
          </div>
        </SectionCard>
      </div>
    </CommandCenterShell>
  )
}
